import { useState, useMemo, useRef } from "react";
import { BsArrowDownUp } from "react-icons/bs";
import Draggable from "react-draggable";
import "./Tasks.css";

const Table = ({
  currentPosts,
  handleCheckboxChange,
  selectedIds,
  selectAll,
  handleSelectAll,
}) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [columnWidths, setColumnWidths] = useState({
    id: 50,
    taskid: 120,
    filename: 180,
    projectid: 110,
    userid: 130,
    wordcounts: 90,
    status: 70,
  });

  const tableRef = useRef(null);
  const resizingCol = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onMouseDown = (e, colKey) => {
    resizingCol.current = colKey;
    startX.current = e.clientX;
    startWidth.current = columnWidths[colKey];
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!resizingCol.current) return;
    const dx = e.clientX - startX.current;
    setColumnWidths((prevWidths) => {
      const newWidth = Math.max(startWidth.current + dx, 50); // minimum width 50px
      return { ...prevWidths, [resizingCol.current]: newWidth };
    });
  };

  const onMouseUp = () => {
    resizingCol.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  const sortedData = useMemo(() => {
    const sortedArray = [...currentPosts];
    if (sortColumn !== "") {
      sortedArray.sort((a, b) => {
        const valueA = a[sortColumn];
        const valueB = b[sortColumn];
        if (sortColumn === "lastmodified") {
          const dateA = new Date(valueA).getTime();
          const dateB = new Date(valueB).getTime();
          return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
        } else {
          if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
          if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
          return 0;
        }
      });
    }
    return sortedArray;
  }, [currentPosts, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const columns = [
    { key: "id", label: "ID", align: "center", bold: true },
    { key: "taskid", label: "Task ID" },
    { key: "filename", label: "Filename" },
    { key: "projectid", label: "Project ID" },
    { key: "userid", label: "User ID" },
    { key: "wordcounts", label: "Wordcounts" },
    { key: "status", label: "Status" },
  ];

  return (
    <div className="table-responsive" ref={tableRef}>
      <table className="table">
        <thead>
          <tr>
            <th
              className="text-center"
              style={{ width: 30, minWidth: 30, position: "relative" }}
            >
              <input
                type="checkbox"
                checked={
                  currentPosts.length > 0 &&
                  currentPosts.every((item) => selectedIds.includes(item.id))
                }
                onChange={handleSelectAll}
              />
            </th>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`text-${col.align || "left"} sortable`}
                style={{
                  width: columnWidths[col.key],
                  minWidth: 50,
                  position: "relative",
                  userSelect: "none",
                }}
                onClick={() => handleSort(col.key)}
              >
                <div className="d-flex align-items-center justify-content-center gap-1">
                  <BsArrowDownUp size={14} />
                  <span className={`fw-bold ${col.bold ? "fw-bold" : ""}`}>
                    {col.label}
                  </span>
                </div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => onMouseDown(e, col.key)}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <tr
              key={row.id}
              style={{
                backgroundColor: selectedIds.includes(row.id) ? "#c1d3f0" : "",
              }}
            >
              <td className="text-center" style={{ width: 10, minWidth: 10 }}>
                <input
                  type="checkbox"
                  className="custom-control-input"
                  checked={selectedIds.includes(row.id) || selectAll}
                  onChange={() => handleCheckboxChange(row.id)}
                />
              </td>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`wrap-text-style text-${col.align || "left"} ${
                    col.bold ? "fw-bold" : ""
                  }`}
                  style={{ width: columnWidths[col.key], minWidth: 50 }}
                >
                  {col.key === "taskid" ? (
                    <span
                      className="taskid-link"
                      onClick={() => setSelectedTask(row)}
                      title="View Task Details"
                    >
                      {row[col.key]}
                    </span>
                  ) : (
                    row[col.key]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal to show task details */}
      {selectedTask && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1200 }}
        >
          <div className="modal-dialog modal-lg">
          <Draggable handle=".modal-header">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Task Details - {selectedTask.taskid}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedTask(null)}
                />
              </div>
              <div
                className="modal-body"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                <ul className="list-group">
                  {Object.entries(selectedTask).map(([key, value]) => (
                  <li
                    key={key}
                    className="list-group-item d-flex justify-content-between align-items-start"
                    style={{
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                      gap: "16px",
                      fontSize: "14px",
                    }}
                  >
                    <strong style={{ minWidth: "30%", fontWeight: 600 }}>{key}</strong>
                    <span
                      style={{
                        fontFamily: "monospace",
                        flex: 1,
                        textAlign: "right",
                        wordBreak: "break-word",
                      }}
                    >
                      {typeof value === "object" && value !== null
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                    </span>
                  </li>
                  ))}
                </ul>


              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedTask(null)}
                >
                  Close
                </button>
              </div>
            </div>
            </Draggable>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
